/**
 * The canonical document/folder row shape, shared by the flat library (`listItems`), the org-wide
 * listing the grouped views draw from (`listOrgDocuments`), the case-tree listing (`listCaseItems`),
 * search (`searchItems`), and the documents store. One definition
 * replaces the hand-maintained copies (`BrowseItem`, `GroupedDocument`, the optimistic-upload literal,
 * `toDocument`) that had already drifted — most visibly, grouped rows omitted `pending_read`, so a
 * document viewed under a case/client lost its OCR badge.
 *
 * ── TWO VIEWS, BECAUSE `pg` AND JSON DISAGREE ────────────────────────────────────────────────────
 *
 * `pg` returns `timestamptz` as a JavaScript `Date` (this repo sets no `setTypeParser`) and JSON
 * turns that into an ISO string on the wire. So the fields are declared ONCE, parameterised by the
 * date representation, and the two views are named — the pattern set by
 * `shared/library/libraryPrompt.ts`:
 *
 *   `ItemRowDb` — what the service functions in `server/services/items/catalog.ts` return. Dates.
 *   `ItemRow`   — what the client receives from `/api/items**`. Strings.
 *
 * `ItemRow` keeps its pre-existing name and meaning; the 16 client files that import it are
 * unaffected.
 */
import type { FolderInfo } from './folderInfo'

interface ItemRowFields<D> {
  id: string
  type: 'folder' | 'document'
  path: string
  name: string
  slug: string
  sort_order: number
  contract_type: string | null
  status: string | null
  file_name: string | null
  compliance_score: number | null
  summary: string | null
  source_backend: 'marcus' | 'google_drive' | 'sharepoint' | null
  onedrive_item_id: string | null
  onedrive_url: string | null
  google_drive_file_id: string | null
  google_drive_url: string | null
  owner_id: string
  /** Folder tree scope: null = general library, a case id = that case's folder tree. Documents keep
   *  this null (their case placements live on case_documents.path); set on case-tree folders and on
   *  rows returned by listCaseItems so the client knows which tree it is browsing. */
  case_id?: string | null
  created_at: D
  updated_at: D
  /**
   * ── THE DERIVED COUNT IS OPTIONAL, AND THAT IS THE HONEST SHAPE ────────────────────────────────
   *
   * It was declared REQUIRED (`number | null`) while not every producer computed it, so the type
   * promised a field the wire did not always carry.
   *
   * Supplied by: `listItems` (`GET /api/items`), `listOrgDocuments` (`?scope=org`), `listCaseItems`
   * (`GET /api/cases/:id/items`) and `searchItems` (folders only). Absent from: `POST /api/items`,
   * `PUT /api/items/:id` (both `RETURNING *`).
   *
   * `null` and absent mean different things and both occur: `null` is "not applicable to this row"
   * (a document has no `child_count`), absent is "this endpoint did not compute it". Every consumer
   * already reads it as `?? 0`.
   *
   * FIVE SIBLINGS WERE REMOVED HERE (MAR-428): `party_count`, `date_count`, `amount_count`,
   * `clause_count` and `term_count`. No component read them, and the listing rows go verbatim to an
   * agent tool result, so each one was an integer per document spent on a model's context for
   * nothing. `getContextDocumentSummaries` (`server/services/items/content.ts`) still computes three
   * counts of its own for the attached-documents block — that is a different row shape and a real
   * consumer.
   */
  child_count?: number | null
  /** How many indexed chunks the document has. Only `searchItems` selects it. */
  chunk_count?: number | null
  /** Derived: this document has a read session awaiting confirmation (a read is queued but not started). */
  pending_read?: boolean
  /**
   * Derived (server, via `identityFrom`): this document's `extracted_metadata` carries a readable
   * identity — what an identity workflow slot's eligibility is judged on (MAR-168). `null` on
   * folders and on rows from endpoints that don't compute it; only ever set from a server row,
   * never client-side (the metadata itself stays server-side).
   */
  has_identity_data?: boolean | null
  /**
   * Derived (server): this image has a grid thumbnail (`items.thumbnail_path IS NOT NULL`, MAR-246).
   * The path itself never reaches the wire — the client asks `GET /api/items/:id/file?variant=thumb`.
   * Absent from rows of endpoints that don't compute it; the grid treats absent as false.
   */
  has_thumbnail?: boolean
  /**
   * The cases this document is linked to, via `case_documents`. Present only on the org-wide listing
   * (`/api/items?scope=org`), which is the grouped sidebar's seed: an empty array is what puts a
   * document in the sidebar's "no case" bucket (MAR-93). Not membership state — once seeded, the
   * cases store owns it.
   */
  case_ids?: string[]
  /**
   * The clients this document is linked to DIRECTLY, via `client_documents` (AlexAmin/marcus#83) —
   * the client-level papers a firm holds before any matter exists. Same listing, same seeding-only
   * contract as `case_ids`.
   *
   * A document reached through one of a client's CASES is not in here; that link belongs to the case.
   * The browser unions the two when it builds a client's group.
   */
  client_ids?: string[]
  /**
   * The chat threads this document is attached to, via `conversation_documents`
   * (AlexAmin/marcus#83). Same listing, same seeding-only contract.
   *
   * The browser reads it as a boolean — a document with no case and no client but a thread is a
   * "From your conversations" document rather than an unattached one — but it ships as the full
   * membership so a future by-thread view needs no server change.
   */
  conversation_ids?: string[]
  /**
   * The sheets this document is linked to, via `sheet_items` (AlexAmin/marcus#93) — the grid rows a
   * document has been dropped into. Same listing, same seeding-only contract as `case_ids`.
   *
   * The browser reads it as a boolean — a document with no case, no client and no thread but a sheet
   * link is a "From your sheets" document rather than an unattached one — but it ships as the full
   * membership so a future by-sheet view needs no server change.
   */
  sheet_ids?: string[]
}

/** Returned by the `server/services/items/catalog.ts` listings. `timestamptz` is a `Date` here. */
export type ItemRowDb = ItemRowFields<Date>
/** Received by the client from `/api/items**`. `timestamptz` has become an ISO string. */
export type ItemRow = ItemRowFields<string>

/**
 * The row as the SQL projection selects it, BEFORE `withIdentityFlag` swaps the raw
 * `extracted_metadata` blob for the one `has_identity_data` boolean. The blob must never reach the
 * wire, which is why this type exists separately rather than `ItemRowDb` carrying it.
 */
export type ItemRowSelect = Omit<ItemRowDb, 'has_identity_data'> & { extracted_metadata: unknown }

/**
 * `GET /api/items?limit=…` — the opt-in paging envelope. Without `limit` the endpoint returns a bare
 * `ItemRow[]`; see `server/api/items/index.get.ts` for why the shape is conditional.
 */
export interface ItemListPage {
  items: ItemRow[]
  total: number
}

/**
 * `GET /api/app/bootstrap` — the `/app` shell's first paint: one folder's listing and the whole
 * folder tree in one request (MAR-948: the documents store used to declare this inline).
 */
interface AppBootstrapFields<D> {
  items: ItemRowFields<D>[]
  folders: FolderInfo[]
}
/** As `server/api/app/bootstrap.get.ts` builds it. */
export type AppBootstrapPayloadDb = AppBootstrapFields<Date>
/** As the client receives it. */
export type AppBootstrapPayload = AppBootstrapFields<string>

/**
 * `POST /api/search/items` — the finder's search results, with how many the ranking produced.
 *
 * The search path used to return a bare array hard-capped at 20 with nothing said about the cut,
 * while the folder path beside it already had `{ items, total }` and a "Cargar más" button
 * (AlexAmin/marcus#169). One pagination concept in the page, not two.
 *
 * `total` is the size of the RANKED SET — what the keyword and vector legs found and the ranker
 * ordered — never the size of the library. `searchDocumentIdsPage` owns that distinction and
 * explains why the alternative ("los 100 más relevantes") was rejected.
 */
export interface ItemSearchPage {
  items: ItemRow[]
  total: number
}

/** The same envelope as `server/services/items/catalog.ts` builds it: `timestamptz` is still a `Date`. */
export interface ItemSearchPageDb {
  items: ItemRowDb[]
  total: number
}

/**
 * `GET /api/items?scope=org` — the rows, plus the counts no client-side row can carry.
 *
 * A case group's true size comes off `Case.documentIds`, which is complete. Nothing else is: the
 * trailing buckets have no entity behind them at all, and a client's DIRECT document list is capped
 * per client like every other group. So every bucket whose "+N more" cannot be derived locally gets
 * its count here.
 *
 * `ungroupedTotal` and `unattachedTotal` look like the same count and are not: "in no case" is a
 * legitimate state (a client-level document is filed, it just has no matter), while "attached to
 * nothing" is no home at all. They are separate fields because they size different things — the
 * first case mode's trailing bucket ("Sin asunto"), the second project mode's virtual "Sin cliente"
 * group (MAR-1006).
 */
/** One sheet that holds documents: its header, and how many it really holds. */
export interface SheetGroupSummary {
  id: string
  name: string
  /** Uncapped — the loaded ids are a prefix, so `total - loaded` is the group's "+N more". */
  total: number
}

/**
 * One chat thread that holds documents: its header, and how many it really holds
 * (AlexAmin/marcus#150 round 2). Same shape as `SheetGroupSummary` and for the same reason — the
 * dedicated Conversaciones mode needs one group per thread, the way Hojas needs one per sheet.
 *
 * `name` is the thread's raw `title`, which can be `''` (a thread with no title yet, or one still
 * on its default). The server does NOT invent a fallback string here — that is a client-side
 * presentation decision, the same one `ChatPanel`/`IconRail` already make for a titleless thread
 * (`conv.title || t('common.untitled')`), not a fact about the thread worth freezing into the wire
 * payload.
 */
export interface ConversationGroupSummary {
  id: string
  name: string
  total: number
}

interface OrgDocumentsFields<D> {
  documents: Array<ItemRowFields<D>>
  /** Documents in no CASE — the sidebar's trailing bucket in case mode. Includes client-only ones. */
  ungroupedTotal: number
  /**
   * Documents linked to ANY chat thread (AlexAmin/marcus#150). Not "and to nothing else": a
   * conversation is a home, so a document that also belongs to a matter is still one of these.
   */
  conversationTotal: number
  /**
   * Documents linked to ANY sheet, counted once each (AlexAmin/marcus#150). NOT the sum of
   * `sheets[].total` — a document in two sheets is one document here — because this sizes the
   * finder's single "de tus hojas" signpost, which names the home rather than the sheets.
   */
  sheetTotal: number
  /**
   * Every sheet holding at least one document, with its name and its uncapped membership count —
   * one browser group per entry (AlexAmin/marcus#150). The ids themselves are not shipped here: the
   * browser reads them off each row's `sheet_ids`, capped at one page per sheet by the server, and
   * `total - loaded` is the group's "+N more".
   */
  sheets: SheetGroupSummary[]
  /**
   * Every chat thread holding at least one document, with its title and its uncapped membership
   * count — one browser group per entry in the dedicated Conversaciones mode (AlexAmin/marcus#150
   * round 2), the same contract `sheets` carries for Hojas. The aggregate "From your conversations"
   * bucket in Clientes mode reads `conversationTotal`/`conversation_ids` instead and ignores this.
   */
  conversations: ConversationGroupSummary[]
  /**
   * Documents attached to NOTHING — no case, no client, no conversation, no sheet. The same
   * predicate the server fetches them by, so the count and the list finally mean one thing.
   * They render in project mode's virtual "Sin cliente" group (`useDocumentGroups`, MAR-1006).
   */
  unattachedTotal: number
  /** Per client id: how many documents link DIRECTLY to it (`client_documents`), uncapped. */
  clientTotals: Record<string, number>
  /**
   * Per case id: how many documents link to it (`case_documents`), under the ACTIVE FILTER
   * (AlexAmin/marcus#169).
   *
   * `Case.documentIds.length` is the browser's other answer to this question and stays the fallback
   * for a case the server did not name — but it comes from `listCases`, which knows nothing about the
   * finder's type/state filter, so under a filter it counts a population the band is not showing.
   */
  caseTotals: Record<string, number>
}

/** Returned by `listOrgDocuments`. */
export type OrgDocumentsPayloadDb = OrgDocumentsFields<Date>
/** Received by the client from `GET /api/items?scope=org`. */
export type OrgDocumentsPayload = OrgDocumentsFields<string>

/**
 * A complete `ItemRow` from whatever partial facts a caller holds.
 *
 * Factory instead of an inline 30-field literal so the shape can never drift from `ItemRow`. Two
 * callers need this: an optimistic mid-upload row (no server row exists yet), and a document the
 * agent surfaced — a search hit or a confirm card — which is attached to the chat before any listing
 * has fetched it. Both need a real row in the documents cache so chips and lists can resolve a name
 * instead of rendering blank (MAR-125).
 */
export function documentRowStub(input: {
  id: string
  name: string
  path?: string
  file_name?: string | null
  status?: string | null
  contract_type?: string | null
  has_identity_data?: boolean | null
}): ItemRow {
  const now = new Date().toISOString()
  return {
    // Only carried when the caller knows it: `upsert` shallow-merges, and a stub asserting `null`
    // would blank a flag a fuller row already established.
    ...(input.has_identity_data !== undefined ? { has_identity_data: input.has_identity_data } : {}),
    id: input.id,
    type: 'document',
    path: input.path ?? '/',
    name: input.name,
    slug: '',
    sort_order: -1,
    contract_type: input.contract_type ?? null,
    status: input.status ?? null,
    file_name: input.file_name ?? null,
    compliance_score: null,
    summary: null,
    source_backend: 'marcus',
    onedrive_item_id: null,
    onedrive_url: null,
    google_drive_file_id: null,
    google_drive_url: null,
    owner_id: '',
    created_at: now,
    updated_at: now,
    child_count: null,
  }
}

/** The optimistic row for a file that is mid-upload, before its server `items` row exists. */
export function emptyUploadRow(input: { id: string; name: string; path: string }): ItemRow {
  return documentRowStub({ ...input, file_name: input.name, status: 'uploading' })
}
